import { useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { BookingRestrictions } from "@tellescope/types-models";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  NumberInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { ChevronDown, ChevronsUpDown, Settings2, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface TemplateConfigRowProps {
  templateId: string;
  restrictions: BookingRestrictions["restrictions"];
  onRestrictionsChange: (restriction: BookingRestrictions["restrictions"]) => void;
  onTemplateChange: (templateId: string) => void;
  onDelete: () => void;
}

export const TemplateConfigRow = ({
  templateId,
  restrictions,
  onRestrictionsChange,
  onTemplateChange,
  onDelete,
}: TemplateConfigRowProps) => {
  const { t } = useTranslation("calendar");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search);
  const templatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: {
      search: debouncedSearch
        ? {
            query: debouncedSearch,
          }
        : undefined,
    },
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });

  const templates = useMemo(() => templatesQuery.data ?? [], [templatesQuery.data]);
  const selectedTemplateFromSearch = useMemo(
    () => templates.find((template) => template.id === templateId),
    [templateId, templates]
  );

  const selectedTemplateQuery = useTellescopeQuery({
    model: "calendar_event_templates",
    method: "getOne",
    request: templateId,
    enabled: !selectedTemplateFromSearch && !!templateId,
  });

  const selectedTemplate = useMemo(
    () => selectedTemplateFromSearch ?? selectedTemplateQuery.data,
    [selectedTemplateFromSearch, selectedTemplateQuery.data]
  );

  return (
    <Accordion type="multiple">
      <AccordionItem value={templateId} className="rounded-md border last:border-b">
        <AccordionHeader className="flex items-center p-3 gap-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                role="combobox"
                aria-expanded={open}
                className="grow justify-between font-normal bg-muted text-muted-foreground text-xs"
              >
                <span className={selectedTemplate ? undefined : "text-muted-foreground"}>
                  {selectedTemplate ? selectedTemplate.displayTitle || selectedTemplate.title : t("Select template...")}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
              onWheelCapture={(e) => e.stopPropagation()}
            >
              <Command shouldFilter={false}>
                <CommandInput placeholder={t("Search templates...")} value={search} onValueChange={setSearch} />
                <CommandList>
                  <CommandEmpty>{t("No templates found")}</CommandEmpty>
                  <CommandGroup>
                    {templatesQuery.data?.map((template) => (
                      <CommandItem
                        value={template.id}
                        key={template.id}
                        onSelect={() => {
                          onTemplateChange(template.id);
                          setOpen(false);
                        }}
                      >
                        {template.displayTitle || template.title}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <span className="flex items-center gap-1">
            <AccordionTrigger
              withoutHeader
              chevronPosition="none"
              className={`flex gap-1 shrink grow-0 items-center [&[data-state=open]>#${templateId}-chevron]:rotate-180`}
              disabled={!templateId}
            >
              <Settings2 size={16} className="text-muted-foreground" />
              <ChevronDown size={12} id={`${templateId}-chevron`} className="text-muted-foreground transition-all" />
            </AccordionTrigger>
            <Button
              variant="ghost"
              onClick={onDelete}
              size="sm-icon"
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 />
            </Button>
          </span>
        </AccordionHeader>
        <AccordionContent className="border-t p-3 flex flex-col gap-4">
          <div className="space-y-2">
            <p className="text-muted-foreground text-xs">{t("Event Settings")}</p>
            <span className="flex items-center gap-1">
              <Button
                variant={restrictions.careTeam ? "default" : "outline"}
                className="border font-light text-xs py-1 h-auto"
                size="sm"
                onClick={() => onRestrictionsChange({ ...restrictions, careTeam: !restrictions.careTeam })}
              >
                {t("Restrict by care team")}
              </Button>
              <Button
                variant={restrictions.state ? "default" : "outline"}
                className="border font-light text-xs py-1 h-auto"
                size="sm"
                onClick={() => onRestrictionsChange({ ...restrictions, state: !restrictions.state })}
              >
                {t("Restrict by credentialed state")}
              </Button>
              <Button
                variant={restrictions.shouldOpenJoinLink ? "default" : "outline"}
                className="border font-light text-xs py-1 h-auto"
                size="sm"
                onClick={() =>
                  onRestrictionsChange({ ...restrictions, shouldOpenJoinLink: !restrictions.shouldOpenJoinLink })
                }
              >
                {t("Auto open join link upon booking")}
              </Button>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground font-normal">{t("Hours in advance")}</Label>
              <NumberInput
                min={0}
                value={restrictions.hoursBefore || undefined}
                onChange={(hoursBefore) => onRestrictionsChange({ ...restrictions, hoursBefore })}
                placeholder={t("Minimum hours before booking")}
                className="bg-gray-100 border-0 text-xs md:text-xs font-light h-auto py-1.5"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground font-normal">
                {t("Hours afterwards to prevent future booking")}
              </Label>
              <NumberInput
                min={0}
                value={restrictions.hoursAfter || undefined}
                onChange={(hoursAfter) => onRestrictionsChange({ ...restrictions, hoursAfter })}
                placeholder={t("Hours to block future bookings")}
                className="bg-gray-100 border-0 text-xs md:text-xs font-light h-auto py-1.5"
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
